import { Box, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import 'leaflet/dist/leaflet.css';
import { useDataFromS3 } from '../hooks/useDataFromS3';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import MapRiskMax from '../components/map_risk_rain';
import ScatterRisk from '../components/scatter_risk';
import HeatmapRisk from '../components/heatmap_risk';
import CommonCard from '../item/common_card';
import HeaderSub from '../item/header_sub';
import Progress from '../item/progress';
import { RISK_HEATMAP_COLOR, SOIL_RISK_COLOR } from '../const/color';
import { H1_RISK_LEGEND } from '../const/legend';
import { BASE_LAYER_LIST } from '../const/map_config';
import { setSrRiskColor } from '../func/color';
import { getTimeAry, convertAreaDataFromMshData, convertCityDataFromMshData, getAllByCode, getPolygonOrMultiPolygonCenter } from '../func/util';
import '../style/legend_risk.css';
import choropleth_area from '../data/area.json';
import choropleth_msh from '../data/msh.json';

const Risk = (props) => {
    const [selectLayer, setSelectLayer] = useState(
        BASE_LAYER_LIST.find(layer => layer.index.toString() === props.defaultUnit) || BASE_LAYER_LIST[1]
    );

    // 市町村、字、メッシュ、個別の選択をクリアするかしないかのフラグ
    const [shouldClearSelect, setShouldClearSelect] = useState(false)
    const [cityLandslideRiskH1, setCityLandslideRiskH1] = useState(null);
    const [areaLandslideRiskH1, setAreaLandslideRiskH1] = useState(null);
    const [mshLandslideRiskH1, setMshLandslideRiskH1] = useState(null);
    const [landslideAllRiskDatetime, setLandslideAllRiskDatetime] = useState(null);
    const [riskScatterAllDatetimeArea, setRiskScatterAllDatetimeArea] = useState(null);
    const [riskScatterAllDatetimeCity, setRiskScatterAllDatetimeCity] = useState(null);
    const [tooltipInfo, setTooltipInfo] = useState(null);

    const [dateArray, setDateArray] = useState(null);
    const [shouldFetchMain, setShouldFetchMain] = useState(false);

    const prevDataARef = useRef(null);

    // 全データ再取得の要否を定期監視する
    const {
        data: datetimeLatest,
        loading: datetimeLatestLoading,
        error: datetimeLatestError,
    } = useDataFromS3(`api/get_datetime_latest`);

    // サーバーの最終更新日時が変更されているか判定
    useEffect(() => {
        if (datetimeLatest && JSON.stringify(datetimeLatest) !== JSON.stringify(prevDataARef.current)) {
            prevDataARef.current = datetimeLatest;
            setShouldFetchMain(true); // dataが変わったらメインデータを取得
        } else {
            setShouldFetchMain(false); // dataが変わってないならメインデータを取得しない
        }
    }, [datetimeLatest]);

    let rain_mtr_url = null

    if (shouldFetchMain) {
        rain_mtr_url = `api/get_risk`;
    }

    // 雨量計情報を取得
    const {
        data: rainMtrData,
        loading: rainMtrLoading,
        error: rainMtrError,
    } = useDataFromS3(rain_mtr_url);

    /**
     * Returns display label for soil risk level
     * @param {number} level - soil risk level value
     * @returns {string} label text for tooltip
     */
    const getLabelTooltipSoilLevel = (level) => {
        const key = `LEVEL_${level}`;
        return SOIL_RISK_COLOR[key]?.label ?? '';
    };

    /**
     * Convert raw area risk data into ApexCharts heatmap series format
     * - Sorts by city_code (descending)
     * - Converts 0 values to a very small number to keep color rendering
     *
     * @param {Array} date_ary - X-axis datetime array
     * @param {Array} area_val_ary - area-based risk values
     * @param {boolean} isOver - unused (can be removed if not needed)
     * @returns {Array} Heatmap series data
     */
    const getHeatmapData = (date_ary, area_val_ary, isOver) => {
        let sorted_ary = []
        if (area_val_ary?.length > 1) {
            sorted_ary = [...area_val_ary].sort((a, b) => (b.city_code - a.city_code));
        } else {
            sorted_ary = area_val_ary
        }
        const chartData = sorted_ary.map((item) => ({
            name: item?.city_name,
            data: item?.risk_val.map((val, i) => ({
                x: date_ary[i],
                y: val === 0 ? 0.0001 : val,
                value: val,
                name: val,
                soil_index_level: val,
                city_code: item?.city_code,
                city_name: item?.city_name,
                area_lat: item?.area_lat,
                area_lng: item?.area_lng
            }))
        }));
        return chartData;
    }

    /**
     * Handles click event on heatmap cell
     * - Centers map to selected city
     * - Resets selection state
     * - Switches map layer to default
     *
     * @param {Object} clickedItem - clicked heatmap cell data
     */
    const heatmapClickEvent = (clickedItem) => {
        const cityCode = clickedItem.city_code;
        props.setBsnMapCenter([clickedItem.area_lng, clickedItem.area_lat])
        props.setRiskMapCenter([clickedItem.area_lng, clickedItem.area_lat])
        setShouldClearSelect(false)
        setSelectLayer(BASE_LAYER_LIST.find(layer => layer.index.toString() === props.defaultUnit) || BASE_LAYER_LIST[1])
        props.setSelectArea([])
        props.setSelectSubCity([cityCode]);
        props.setSelectMsh([]);
        props.setSelectBsn([]);
    }

    /**
     * Handles click event on scatter point
     * - Moves map center to selected point
     * - Updates selection based on point type (area or city)
     *
     * @param {Object} point - scatter point meta data
     */
    const scatterClickEvent = (point) => {
        props.setRiskMapCenter([point?.area_lng, point?.area_lat])
        setShouldClearSelect(false)
        setSelectLayer(BASE_LAYER_LIST.find(layer => layer.index.toString() === props.defaultUnit) || BASE_LAYER_LIST[1])
        props.setSelectMsh([]);
        if (point?.type === "area") {
            props.setSelectSubCity([]);
            props.setSelectArea([point?.area_code]);
        }
        if (point?.type === "city") {
            props.setSelectSubCity([point?.city_code]);
            props.setSelectArea([]);
        }
    }

    // Extracts 1-hour rainfall values (Y-axis) from scatter data
    const soil_rain_h1_ary = (scatterData) => {
        return scatterData.map((obj) => obj.h1_h24_val[0]);
    }

    // Extracts 24-hour rainfall values (X-axis) from scatter data
    const soil_rain_h24_ary = (scatterData) => {
        return scatterData.map((obj) => obj.h1_h24_val[1]);
    }

    // Extracts 24-hour rainfall values (X-axis) from scatter data
    const soil_rain_dc_tank_ary = (scatterData) => {
        return scatterData.map((obj) => obj.h1_dc_tank_of_val[1]);
    }

    /**
     * Generates color array for scatter chart points
     * - Supports slider-based filtering
     *
     * @param {Array} chartData - scatter source data
     * @param {boolean} isSlider - slider enabled flag
     * @param {Array} value - slider range [min, max]
     * @returns {Array<string>} color list
     */
    const soilRainGetColors = (chartData, isSlider, value) => {
        let res = []
        if (isSlider) {
            for (let i = value[1]; i > value[0]; i--) {
                if (chartData?.length - 1 > i) {
                    res.push(setSrRiskColor(chartData[i]?.soil_index_level).color)
                }
            }
        } else {
            if (chartData?.length > 0) {
                chartData.map(item => {
                    res.push(setSrRiskColor(item?.soil_index_level).color)
                })
            }
        }
        return res;
    }

    // ヒートマップのカラー
    const colorScale = () => {
        return {
            ranges: RISK_HEATMAP_COLOR
        }
    }

    /**
     * Converts mesh risk data into scatter-ready city risk points
     * - Calculates max risk values
     * - Extracts centroid coordinates
     *
     * @param {Array} mshMasterFeatures
     * @param {Array} data
     * @returns {Array} scatter plot city data
     */
    const convertCityDataRiskFromMshData = (mshMasterFeatures, data) => {
        const idToFeatureMap = {};
        for (const feature of mshMasterFeatures) {
            let id = feature.properties.id;
            idToFeatureMap[id] = feature;
        }

        // 一時格納用：city_code をキーにして統合
        const cityDataMap = {};

        for (const d of data) {
            let matchedFeature = idToFeatureMap[d.code];
            if (!matchedFeature) continue;

            let { city_code, city_name } = matchedFeature.properties;
            if (!city_code) continue;

            let lat_lng = getPolygonOrMultiPolygonCenter(matchedFeature.geometry.coordinates);
            let max_risk = Array.isArray(d.rsk_dc_value_num) ? Math.max(...d.rsk_dc_value_num) : null;
            let max_dc_tank_of = Array.isArray(d.dc_tank_of_num) ? Math.max(...d.dc_tank_of_num) : null;
            let max_h1 = Array.isArray(d.h1_dc_value_num) ? Math.max(...d.h1_dc_value_num) : null;
            let max_h24 = Array.isArray(d.h24_dc_value_num) ? Math.max(...d.h24_dc_value_num) : null;
            let max_h48 = Array.isArray(d.h48_dc_value_num) ? Math.max(...d.h48_dc_value_num) : null;
            let max_soil = Array.isArray(d.soil_index_level) ? Math.max(...d.soil_index_level) : null;

            if (!cityDataMap[city_code]) {
                cityDataMap[city_code] = {
                    msh_code: d.code, // 最初の msh_code を使う（代表）
                    city_code,
                    name: city_name || '',
                    risk_val: max_risk,
                    h1_val: max_h1,
                    h24_val: max_h24,
                    h48_val: max_h48,
                    dc_tank_of_val: max_dc_tank_of,
                    h1_dc_tank_of_val: [max_h1, max_dc_tank_of],
                    h1_h24_val: [max_h1, max_h24],
                    area_lng: lat_lng[0],
                    area_lat: lat_lng[1],
                    soil_index_level: max_soil,
                };
            } else {
                // 既存の最大値と比較して更新
                const existing = cityDataMap[city_code];
                existing.risk_val = Math.max(existing.risk_val ?? -Infinity, max_risk ?? -Infinity);
                existing.h1_val = Math.max(existing.h1_val ?? -Infinity, max_h1 ?? -Infinity);
                existing.h24_val = Math.max(existing.h24_val ?? -Infinity, max_h24 ?? -Infinity);
                existing.h48_val = Math.max(existing.h48_val ?? -Infinity, max_h48 ?? -Infinity);
                existing.dc_tank_of_val = Math.max(existing.dc_tank_of_val ?? -Infinity, max_dc_tank_of ?? -Infinity);
                existing.h1_dc_tank_of_val = [existing.h1_val, existing.dc_tank_of_val];
                existing.h1_h24_val = [existing.h1_val, existing.h24_val];
                existing.soil_index_level = Math.max(existing.soil_index_level ?? -Infinity, max_soil ?? -Infinity);
            }
        }
        return Object.values(cityDataMap);
    };

    /**
     * 全日時断面のリスクに地域情報を付与する
     * @param {Object} features - 地域情報取得用のデータ
     * @param {Object} data - 全日時断面のリスクデータ
     * @returns {Array} 全日時断面のリスクと地域情報
     */
    const getAllByDateTimeWithCityInfo = (features, data) => {
        const result = data.map(item => {
            const city_code = item.code
            // 対象字コードの feature を抽出
            const filteredFeatures = features.find(f =>
                f.properties.city_code === city_code
            );
            // featureから字名を取得
            const city_name = filteredFeatures.properties.city_name

            // featureから緯度経度を取得
            const lat_lng = getPolygonOrMultiPolygonCenter(filteredFeatures?.geometry?.coordinates)
            const area_lng = lat_lng[0]
            const area_lat = lat_lng[1]
            const risk_val = item.soil_index_level 
            return { city_code, city_name, area_lat, area_lng, risk_val };
        });
        return result
    };

    /**
     * 全日時断面のリスクに地域情報を付与する
     * @param {Object} features - 地域情報取得用のデータ
     * @param {Object} data - 全日時断面のリスクデータ
     * @returns {Array} 全日時断面のリスクと地域情報
     */
    const getAllByDateTimeWithAreaInfo = (features, data) => {
        const result = data.map(item => {
            const area_code = item.code
            // 対象字コードの feature を抽出
            const filteredFeatures = features.find(f =>
                f.properties.area_code === area_code
            );
            // featureから字名を取得
            const city_name = filteredFeatures.properties?.city_name;
            const area_name = filteredFeatures.properties?.area_name;
            const name = filteredFeatures.properties?.name;

            // featureから緯度経度を取得
            let max_risk = Array.isArray(item.rsk_dc_value_num) ? Math.max(...item.rsk_dc_value_num) : null;
            let max_dc_tank_of = Array.isArray(item.dc_tank_of_num) ? Math.max(...item.dc_tank_of_num) : null;
            let max_h1 = Array.isArray(item.h1_dc_value_num) ? Math.max(...item.h1_dc_value_num) : null;
            let max_h24 = Array.isArray(item.h24_dc_value_num) ? Math.max(...item.h24_dc_value_num) : null;
            let max_soil = Array.isArray(item.soil_index_level) ? Math.max(...item.soil_index_level) : null;
            let max_h48 = Array.isArray(item.h48_dc_value_num) ? Math.max(...item.h48_dc_value_num) : null;
            const lat_lng = getPolygonOrMultiPolygonCenter(filteredFeatures?.geometry?.coordinates)
            const area_lng = lat_lng[0]
            const area_lat = lat_lng[1]
            return {
                area_code,
                city_name,
                area_name,
                name,
                area_lat,
                area_lng,
                risk_val: max_risk,
                soil_index_level: max_soil,
                h1_dc_tank_of_val: [max_h1, max_dc_tank_of],
                h1_h24_val: [max_h1, max_h24],
            };
        });
        return result
    };

    /**
     * Calculates maximum H1 risk per code group
     * - Supports grouping by substring (city, area, mesh)
     *
     * @param {Array} data
     * @param {number} substringLength
     * @returns {Array}
     */
    const getMaxRiskH1ByCode = (data, substringLength = 0) => {
        const resultMap = {};
        data.forEach(({ code, rsk_dc_value_num, h1_dc_value_num, soil_index_level }) => {
            let groupCode = code
            if (substringLength > 0)
                groupCode = String(code).slice(0, substringLength); // 先頭5桁（例：11111）

            // const maxRisk = Math.max(...rsk_dc_value_num.slice(6));
            const maxH1 = Math.max(...h1_dc_value_num.slice(6));
            const maxSoil = Math.max(...soil_index_level.slice(6));

            if (!resultMap[groupCode]) {
                resultMap[groupCode] = { code: Number(groupCode), risk_val: maxSoil, h1_val: maxH1 };
            } else {
                resultMap[groupCode].risk_val = Math.max(resultMap[groupCode].risk_val, maxSoil);
                resultMap[groupCode].h1_val = Math.max(resultMap[groupCode].h1_val, maxH1);
            }
        });

        // 最後に配列にして返す
        const result = Object.values(resultMap);
        return result;
    };

    /**
     * Builds ApexCharts scatter series (H1 vs H24)
     * - Groups points by risk color
     *
     * @param {Array} chartData
     * @param {boolean} isSlider
     * @param {Array} value
     * @returns {Array}
     */
    const soilRainGetSeriesH1H24 = (chartData, isSlider, value) => {
        if (!chartData?.length) return [];
        const filteredData = isSlider ? chartData.slice(value[0], value[1] + 1) : chartData;
        const colorGroups = {};
        filteredData.forEach(item => {
            const [y, x] = item.h1_h24_val;
            const color = setSrRiskColor(item?.soil_index_level).color;

            if (!colorGroups[color]) {
                colorGroups[color] = {
                    name: item.name,
                    data: [],
                    color: color,
                };
            }

            colorGroups[color].data.push({
                x, y,
                meta: {
                    name: item.area_name,
                    city_name: item.city_name,
                    area_code: item.area_code,
                    area_name: item.area_name,
                    area_lat: item.area_lat,
                    area_lng: item.area_lng,
                    soil_index_level: item.soil_index_level,
                    h1_h24_val: item.h1_h24_val,
                    type: "area"
                }
            });
        });
        return Object.values(colorGroups);
    };

    /**
     * Builds city-level scatter series (H1 vs DC Tank)
     * - Adds dynamic marker size by risk level
     *
     * @param {Array} chartData
     * @param {boolean} isSlider
     * @param {Array} value
     * @returns {Array}
     */
    const soilRainGetSeriesH1DcTank = (chartData, isSlider, value) => {
        if (!chartData?.length) return [];
        const filteredData = isSlider ? chartData.slice(value[0], value[1] + 1) : chartData;
        const colorGroups = {};
        filteredData.forEach(item => {
            const [y, x] = item.h1_dc_tank_of_val;
            const color = setSrRiskColor(item?.soil_index_level).color;

            if (!colorGroups[color]) {
                colorGroups[color] = {
                    name: item.name,
                    data: [],
                    color: color,
                };
            }

            colorGroups[color].data.push({
                x, y,
                meta: {
                    name: item.area_name,
                    city_name: item.city_name,
                    area_code: item.area_code,
                    area_name: item.area_name,
                    area_lat: item.area_lat,
                    area_lng: item.area_lng,
                    soil_index_level: item.soil_index_level,
                    h1_dc_tank_of_val: item.h1_dc_tank_of_val,
                    type: "area"
                }
            });
        });
        return Object.values(colorGroups);
    };

    /**
     * Builds city-level scatter series (H1 vs DC Tank of City)
     * - Adds dynamic marker size by risk level
     *
     * @param {Array} chartData
     * @param {boolean} isSlider
     * @param {Array} value
     * @returns {Array}
     */
    const soilRainGetSeriesH1DcTankCity = (chartData, isSlider, value) => {
        if (!chartData?.length) return [];
        const filteredData = isSlider ? chartData.slice(value[0], value[1] + 1) : chartData;
        const colorGroups = {};
        filteredData.forEach(item => {
            const [y, x] = item.h1_dc_tank_of_val;
            const color = setSrRiskColor(item?.soil_index_level)?.color;
            const sizePoint = setSrRiskColor(item?.soil_index_level)?.size_point;
            if (!colorGroups[color]) {
                colorGroups[color] = {
                    name: item.name,
                    data: [],
                    color: color,
                    marker: {
                        size: sizePoint
                    }
                };
            }

            colorGroups[color].data.push({
                x, y,
                meta: {
                    name: item.name,
                    area_lat: item.area_lat,
                    area_lng: item.area_lng,
                    city_code: item.city_code,
                    city_name: item.city_name,
                    soil_index_level: item.soil_index_level,
                    h1_dc_tank_of_val: item.h1_dc_tank_of_val,
                    risk_val: item.risk_val,
                    type: "city"
                }
            });
        });
        return Object.values(colorGroups);
    };

    const InfoRow = ({ label, value }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography fontSize={13}>{label}</Typography>
            <Typography fontSize={13} fontWeight="bold">{value}</Typography>
        </Box>
    );

    const renderTooltipBody = (tooltipKey, tooltipInfo) => {
        if (!tooltipInfo?.data) return null;
        const d = tooltipInfo.data;
        switch (tooltipKey) {
            case 'scatter_rain_h1_dc_tank':
                return (<>
                    <InfoRow label="土壌雨量指数" value={d.h1_dc_tank_of_val?.[1]} />
                    <InfoRow label="1時間雨量" value={d.h1_dc_tank_of_val?.[0]} />
                    <InfoRow label="警戒基準相当" value={d.soil_index_level} />
                    <InfoRow label="しきい値" value={"警報基準相当"} />
                    <InfoRow label="しきい値名" value={"新規しきい値"} />
                    <InfoRow label="範囲" value={getLabelTooltipSoilLevel(d.soil_index_level)} />
                </>
                );

            case 'scatter_rain_h1_h24':
                return (<>
                    <InfoRow label="24時間雨量" value={d.h1_h24_val?.[1]} />
                    <InfoRow label="1時間雨量" value={d.h1_h24_val?.[0]} />
                    <InfoRow label="警戒基準相当" value={d.soil_index_level} />
                    <InfoRow label="しきい値" value={"警報基準相当"} />
                    <InfoRow label="しきい値名" value={"新規しきい値"} />
                    <InfoRow label="範囲" value={getLabelTooltipSoilLevel(d.soil_index_level)} />
                </>
                );
            case 'scatter_rain_h1_dc_tank_city':
                return (<>
                    <InfoRow label="土壌雨量指数" value={d.h1_dc_tank_of_val[1]} />
                    <InfoRow label="1時間雨量" value={d.h1_dc_tank_of_val[0]} />
                    <InfoRow label="土砂災害発生リスク" value={d.risk_val} />
                    <InfoRow label="警報基準相当" value={d.soil_index_level} />
                    <InfoRow label="しきい値" value={"警報基準相当"} />
                    <InfoRow label="しきい値名" value={"新規しきい値"} />
                    <InfoRow label="範囲" value={getLabelTooltipSoilLevel(d.soil_index_level)} />
                </>
                )
            case 'heatmap_rain_city':
                return (<>
                    <InfoRow label="日時" value={d.x} />
                    <InfoRow label="市町村名" value={d.city_name} />
                    <InfoRow label="しきい値" value={"警報基準相当"} />
                    <InfoRow label="しきい値名" value={"新規しきい値"} />
                    <InfoRow label="範囲" value={getLabelTooltipSoilLevel(d.soil_index_level)} />
                </>
                );

            default:
                return null;
        }
    };

    const setRiskColor = (POINT, risk) => {
        return POINT[`LEVEL_${risk}`]?.color || POINT.LEVEL_1.color;
    };

    /**
     * Prepare master data parameters based on selected codes
     * - Filters mesh features
     * - Extracts related area and mesh codes
     *
     * @param {Array} keyAry - selected codes
     * @param {string} keyName - feature property key
     * @returns {Object} prepared master data
     */
    const getParams = (keyAry, keyName = '') => {
        const features_area = choropleth_area.features
        const features_msh = choropleth_msh.features

        // メッシュデータ取得
        // const keyAryStr = keyAry.map(key => String(key));
        // const masterMshFeatures = features_msh.filter(f =>
        //     keyAryStr.includes(f.properties[keyName].toString())
        // );

        const keySet = new Set(keyAry.map(String));
        const masterMshFeatures = features_msh.filter(f =>
            keySet.has(String(f.properties[keyName]))
        );

        // メッシュデータの properties.area_code を配列で取得(これが対象の字コード)
        const area_code_ary = [...new Set(masterMshFeatures.map(feature => feature.properties.area_code))];
        // 字のマスターデータ取得
        const masterAreaFeatures = features_area.filter(f =>
            area_code_ary.includes(f.properties.id)
        );
        // メッシュデータの properties.id を配列で取得(これが対象のメッシュコード)
        const msh_code_ary = masterMshFeatures.map(feature => feature.properties.id);

        return {
            msh_code_ary, area_code_ary, masterMshFeatures, masterAreaFeatures
        };
    }

    // ローディング開始
    const startLoading = () => {
        props.setReady(prev => {
            const updated = Object.fromEntries(
                Object.keys(prev).map(key => [key, false])
            );
            return updated;
        });
    }

    useEffect(() => {
        // ローディング開始
        startLoading()
        const fetchData = async () => {
            let params_obj = {}
            let date_ary = []
            let msh_landslide_risk_all_datetime = [];

            let city_landslide_risk_all_datetime = [];
            let area_landslide_risk_all_datetime = [];
            let landslide_risk_all_datetime = [];

            let area_landslide_risk_h1_max = [];
            let city_landslide_risk_h1_max = [];
            let msh_landslide_risk_h1_max = [];
            let risk_scatter_all_datetime_area = [];
            let risk_scatter_all_datetime_city = [];

            if (props.selectArea && Array.isArray(props.selectArea) && props.selectArea.length > 0) {
                params_obj = getParams(props.selectArea, 'area_code')
            } else if (props.selectMsh && Array.isArray(props.selectMsh) && props.selectMsh.length > 0) {
                params_obj = getParams(props.selectMsh, 'id')
            } else if (props.selectSubCity && Array.isArray(props.selectSubCity) && props.selectSubCity.length > 0) {
                params_obj = getParams(props.selectSubCity, 'city_code')
            } else {
                params_obj = getParams(props.selectCitys, 'city_code')
            }

            if (Object.keys(params_obj).length > 0) {
                // 日時の配列を取得
                date_ary = getTimeAry(datetimeLatest, false)

                msh_landslide_risk_all_datetime = getAllByCode(params_obj.msh_code_ary, rainMtrData, props.h6Switch)
                if (msh_landslide_risk_all_datetime?.length > 0) {
                    // 字コードごとのリスクと雨量のオブジェクト配列を取得
                    area_landslide_risk_all_datetime = convertAreaDataFromMshData(params_obj.masterMshFeatures, msh_landslide_risk_all_datetime)
                    // 地図の字レイヤー用のデータ
                    area_landslide_risk_h1_max = getMaxRiskH1ByCode(area_landslide_risk_all_datetime, 0)
                    // // 地図の市町村レイヤー用のデータ
                    city_landslide_risk_h1_max = getMaxRiskH1ByCode(area_landslide_risk_all_datetime, 5)
                    // 地図のメッシュレイヤー用のデータ
                    msh_landslide_risk_h1_max = getMaxRiskH1ByCode(msh_landslide_risk_all_datetime, 0)
                    // 散布図の全日時断面用のデータ
                    risk_scatter_all_datetime_area = getAllByDateTimeWithAreaInfo(params_obj.masterMshFeatures, area_landslide_risk_all_datetime)
                    // 散布図の累加雨量用のデータ
                    risk_scatter_all_datetime_city = convertCityDataRiskFromMshData(params_obj.masterMshFeatures, msh_landslide_risk_all_datetime)
                    // チャートの全日時断面用のデータ
                    city_landslide_risk_all_datetime = convertCityDataFromMshData(params_obj.masterMshFeatures, msh_landslide_risk_all_datetime)
                    // チャートの全日時断面用のデータ
                    landslide_risk_all_datetime = getAllByDateTimeWithCityInfo(params_obj.masterMshFeatures, city_landslide_risk_all_datetime);
                }
            }
            // for map 警報基準相当 
            setCityLandslideRiskH1(city_landslide_risk_h1_max?.length > 0 ? city_landslide_risk_h1_max : null)
            setAreaLandslideRiskH1(area_landslide_risk_h1_max?.length > 0 ? area_landslide_risk_h1_max : null)
            setMshLandslideRiskH1(msh_landslide_risk_h1_max?.length > 0 ? msh_landslide_risk_h1_max : null)

            // for scatter 散布図用データ
            setRiskScatterAllDatetimeArea(risk_scatter_all_datetime_area?.length > 0 ? risk_scatter_all_datetime_area : null)
            setRiskScatterAllDatetimeCity(risk_scatter_all_datetime_city?.length > 0 ? risk_scatter_all_datetime_city : null)

            // For chart 市町村名、日時ごとの警報基準相当
            setLandslideAllRiskDatetime(landslide_risk_all_datetime?.length > 0 ? landslide_risk_all_datetime : null)

            // 日時
            setDateArray(date_ary.length > 0 ? date_ary : null)
        };
        if (datetimeLatest && rainMtrData) {
            fetchData();
        }
    }, [datetimeLatest, rainMtrData,
        props.selectCitys, props.selectSubCity, props.selectArea, props.selectMsh, props.selectBsn,
        props.h6Switch
    ]);

    if (datetimeLatestLoading || datetimeLatest === undefined
        || (datetimeLatestError && datetimeLatestError.message.includes("Received HTML"))
        || rainMtrLoading || rainMtrData === undefined
        || (rainMtrError && rainMtrError.message.includes("Received HTML"))
    ) {
        return <Progress />
    }

    if (datetimeLatestError || rainMtrError) {
        return (
            <>
                <Typography
                    sx={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: 'white'
                    }}
                >
                    申し訳ございません、データ取得に失敗しました。
                </Typography>
                <Typography
                    sx={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: 'white'
                    }}
                >
                    恐れ入りますが、しばらく時間をおいてから再度お試しください。
                </Typography>
            </>
        )
    }

    return (
        <>
            <Box
                sx={{
                    // グローバルヘッダーの「36px」と時刻表示分の「17px」、大枠のGridのマージン上下「8px」ずつを差し引きく
                    height: { xs: '100%', sm: 'calc(100dvh - 36px - 17px - 8px - 8px)' }, // 親が高さを持つ
                    boxSizing: 'border-box'
                }}
            >
                <HeaderSub
                    pageTitle={""}
                    dateTime={datetimeLatest && datetimeLatest[12]}
                />
                <Grid container
                    spacing={0}
                    columns={12}
                    sx={{ height: '100%', width: '100%', padding: 0, m: 0 }} // Grid全体の高さ100%
                >
                    <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                        <CommonCard
                            child={
                                <MapRiskMax
                                    selectSubCity={props.selectSubCity}
                                    setSelectSubCity={props.setSelectSubCity}
                                    selectArea={props.selectArea}
                                    setSelectArea={props.setSelectArea}
                                    selectMsh={props.selectMsh}
                                    setSelectMsh={props.setSelectMsh}
                                    selectBsn={props.selectBsn}
                                    setSelectBsn={props.setSelectBsn}
                                    selectCitys={props.selectCitys}
                                    cityLandslideRiskH1={cityLandslideRiskH1}
                                    areaLandslideRiskH1={areaLandslideRiskH1}
                                    mshLandslideRiskH1={mshLandslideRiskH1}
                                    riskMapZoom={props.riskMapZoom}
                                    riskMapCenter={props.riskMapCenter}
                                    selectLayer={selectLayer}
                                    setSelectLayer={setSelectLayer}
                                    setRiskColor={(risk) => setRiskColor(SOIL_RISK_COLOR, risk)}
                                    shouldClearSelect={shouldClearSelect}
                                    setShouldClearSelect={setShouldClearSelect}
                                    tooltipLabel={"警報基準相当"}
                                    legendLabel={"警報基準相当"}
                                />
                            }
                            title={"警報基準相当"}
                            xsHeight="300px"
                            height='calc(50dvh - (36px * 0.5) - (17px * 0.5) - (8px * 0.5) - (8px * 0.5) - 31px - 5px)'
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                        <Stack spacing={0}>
                            {/* 字名ごとの1時間雨量,土壤雨量指数 */}
                            <CommonCard
                                child={
                                    <>
                                        {H1_RISK_LEGEND()}
                                        <ScatterRisk
                                            dateArray={dateArray}
                                            distance={riskScatterAllDatetimeArea?.length}
                                            scatterData={riskScatterAllDatetimeArea}
                                            getSeries={soilRainGetSeriesH1DcTank}
                                            x_ary={soil_rain_dc_tank_ary}
                                            y_ary={soil_rain_h1_ary}
                                            maxValX={200}
                                            maxValY={30}
                                            isShowLabelMaxXMm={false}
                                            scatterClickEvent={scatterClickEvent}
                                            setTooltipInfo={setTooltipInfo}
                                            tooltipInfo={tooltipInfo}
                                            renderBody={renderTooltipBody}
                                            isReadyKey={['scatter_rain_h1_dc_tank']}
                                            isReadyFlg={props.isReady.scatter_rain_h1_dc_tank}
                                            setReady={props.setReady}
                                            selectCitys={props.selectCitys}
                                            isShowDataLabel={false}
                                            height={'calc(100% - 20px)'}
                                            textX={"土壌雨量指数"}
                                            textY={"1時間雨量"}
                                            whiteSpace={'normal'}
                                            tooltipKey={"scatter_rain_h1_dc_tank"}
                                        />
                                    </>
                                }
                                title={"字名ごとの1時間雨量,土壤雨量指数"}
                                xsHeight="300px"
                                height='calc(50dvh - (36px * 0.5) - (17px * 0.5) - (8px * 0.5) - (8px * 0.5) - 31px - 5px)'
                            />
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                        <Stack spacing={0}>
                            {/* 字名ごとの1時間雨量,24時間雨量 */}
                            <CommonCard
                                child={
                                    <>
                                        {H1_RISK_LEGEND()}
                                        <ScatterRisk
                                            dateArray={dateArray}
                                            distance={riskScatterAllDatetimeArea?.length}
                                            scatterData={riskScatterAllDatetimeArea}
                                            getSeries={soilRainGetSeriesH1H24}
                                            getColors={soilRainGetColors}
                                            x_ary={soil_rain_h24_ary}
                                            y_ary={soil_rain_h1_ary}
                                            maxValX={200}
                                            maxValY={30}
                                            scatterClickEvent={scatterClickEvent}
                                            setTooltipInfo={setTooltipInfo}
                                            tooltipInfo={tooltipInfo}
                                            renderBody={renderTooltipBody}
                                            isReadyKey={['scatter_rain_h1_h24']}
                                            isReadyFlg={props.isReady.scatter_rain_h1_h24}
                                            setReady={props.setReady}
                                            selectCitys={props.selectCitys}
                                            isShowDataLabel={false}
                                            height={'calc(100% - 20px)'}
                                            textX={"24時間雨量"}
                                            textY={"1時間雨量"}
                                            whiteSpace={'normal'}
                                            tooltipKey={"scatter_rain_h1_h24"}
                                        />
                                    </>
                                }
                                title={"字名ごとの1時間雨量,24時間雨量"}
                                xsHeight="300px"
                                height='calc(50dvh - (36px * 0.5) - (17px * 0.5) - (8px * 0.5) - (8px * 0.5) - 31px - 5px)'
                            />
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 9 }}>
                        <CommonCard
                            child={
                                <HeatmapRisk
                                    distance={18}
                                    limitDataCount={200}
                                    defaultUnit={props.defaultUnit}
                                    dateArray={dateArray}
                                    allRiskDatetime={landslideAllRiskDatetime}
                                    getHeatmapData={getHeatmapData}
                                    heatmapClickEvent={heatmapClickEvent}
                                    colorScale={colorScale}
                                    setTooltipInfo={setTooltipInfo}
                                    tooltipInfo={tooltipInfo}
                                    renderBody={renderTooltipBody}
                                    tooltipKey={"heatmap_rain_city"}
                                    isReadyKey={['heatmap_rain_city']}
                                    isReadyFlg={props.isReady.heatmap_rain_city}
                                    setReady={props.setReady}
                                />
                            }
                            title={"市町村名、日時ごとの警報基準相当"}
                            xsHeight="400px"
                            height='calc(50dvh - (36px * 0.5) - (17px * 0.5) - (8px * 0.5) - (8px * 0.5) - 31px - 5px)'
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                        <Stack spacing={0} height={{ xs: '100%', sm: 'calc(50dvh - 18px - 9px - 8px)' }} >
                            <CommonCard
                                child={
                                    <ScatterRisk
                                        dateArray={dateArray}
                                        distance={riskScatterAllDatetimeCity?.length}
                                        scatterData={riskScatterAllDatetimeCity}
                                        getSeries={soilRainGetSeriesH1DcTankCity}
                                        x_ary={soil_rain_dc_tank_ary}
                                        y_ary={soil_rain_h1_ary}
                                        maxValX={200}
                                        maxValY={30}
                                        isShowLabelMaxXMm={false}
                                        scatterClickEvent={scatterClickEvent}
                                        setTooltipInfo={setTooltipInfo}
                                        tooltipInfo={tooltipInfo}
                                        renderBody={renderTooltipBody}
                                        isReadyKey={['scatter_rain_h1_dc_tank_city']}
                                        isReadyFlg={props.isReady.scatter_rain_h1_dc_tank_city}
                                        setReady={props.setReady}
                                        selectCitys={props.selectCitys}
                                        isShowDataLabel={true}
                                        height={'100%'}
                                        textX={"土壌雨量指数"}
                                        textY={"1時間雨量"}
                                        whiteSpace={'normal'}
                                        tooltipKey={"scatter_rain_h1_dc_tank_city"}
                                    />
                                }
                                title={"市町村ごとの1時間最大雨量,土壤雨量指数"}
                                xsHeight="300px"
                                height='calc(50dvh - (36px * 0.5) - (17px * 0.5) - (8px * 0.5) - (8px * 0.5) - 31px - 5px)'
                            />
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </>
    )
}
export default Risk;